import type { ChapterSummary } from '@listening/api-contract/schemas';
import type { AudioItemDetail } from '@listening/api-contract/legacy-client-types';
import { assertDefined } from '@listening/utils/ts-utils';
import React, {
  type ComponentProps,
  Fragment,
  useImperativeHandle,
  useState,
} from 'react';
import { useGetterEveryFrameFps } from '../utils';
import { useDocumentSummary } from './core';
import { useAudioChunkPlayer, useAudioChunkPlayerSeekMethods } from './hooks';

type TickMarkStyle = {
  position: 'absolute';
  transform: 'translateX(-50%)';
  // left: `${number}%`;
  left: string;
};

const baseTickStyleProps = {
  transform: 'translateX(-50%)',
  position: 'absolute',
} as const;

type ChapterTickMarkRenderProps = {
  chapterSummary: ChapterSummary;
  chapterIdx: number;
  style: TickMarkStyle;
};

export type AudioChunkSeekBarHandle = {
  seekRelative: (relativeMs: number) => void;
  seekToMsInDoc: (value: number) => void;
};

/**
 * Should render all audio chunks end to end.
 *
 * @param renderSeekBar - Must have position: relative
 *
 */
export function AudioChunkSeekBar({
  ref,
  renderSeekBar,
  ChapterTickMarkRender,
  AudioChunkTickMarkRender,
}: {
  AudioChunkTickMarkRender?: (
    props: ChapterTickMarkRenderProps & { ttsId: string; ttsIdx: number },
  ) => React.ReactNode;
  ChapterTickMarkRender?: (
    props: ChapterTickMarkRenderProps,
  ) => React.ReactNode;
  renderSeekBar: (props: {
    min: number;
    max: number;
    value: number;
    onValueChanged: (value: number) => void;
    onValueCommit: (value: number) => void;
    tickMarkChildren: React.ReactNode;
  }) => React.ReactNode;
} & { ref?: React.RefObject<AudioChunkSeekBarHandle | null> }) {
  const {
    seekToMsInDoc,
    currentChunkStartMsInDoc,
    estimatedTotalAudioDuration,
    durationsPrefixSums,
  } = useAudioChunkPlayerSeekMethods();

  const { player } = useAudioChunkPlayer();
  const getPosMs = player.getPositionMs;
  const { documentSummary } = useDocumentSummary();

  const shouldRenderTickMarks =
    documentSummary && (!!ChapterTickMarkRender || !!AudioChunkTickMarkRender);

  const [isDragging, setIsDragging] = useState(false);
  const [draggingValue, setDraggingValue] = useState(0);

  useImperativeHandle(
    ref,
    () => ({
      seekRelative: (relativeMs: number) => {
        const currentAudioPosInDocument = currentChunkStartMsInDoc + getPosMs();
        seekToMsInDoc(currentAudioPosInDocument + relativeMs);
      },
      seekToMsInDoc,
    }),
    [currentChunkStartMsInDoc, getPosMs, seekToMsInDoc],
  );

  const currentChunkPos = useGetterEveryFrameFps(getPosMs, 1);

  const currentAudioPosInDocument = currentChunkStartMsInDoc + currentChunkPos;
  const value = isDragging ? draggingValue : currentAudioPosInDocument;

  return renderSeekBar({
    max: estimatedTotalAudioDuration,
    min: 0,
    onValueChanged: (value) => {
      setIsDragging(true);
      setDraggingValue(value);
    },
    onValueCommit: (value) => {
      setIsDragging(false);
      seekToMsInDoc(value);
    },
    tickMarkChildren: shouldRenderTickMarks && (
      <TickMarksRenderer
        AudioChunkTickMarkRender={AudioChunkTickMarkRender}
        ChapterTickMarkRender={ChapterTickMarkRender}
        documentSummary={documentSummary}
        durationsPrefixSums={durationsPrefixSums}
      />
    ),
    value,
  });
}

function TickMarksRenderer({
  ChapterTickMarkRender,
  AudioChunkTickMarkRender,
  durationsPrefixSums,
  documentSummary,
}: {
  documentSummary: AudioItemDetail;
  durationsPrefixSums: Array<Array<number>>;
  ChapterTickMarkRender: ComponentProps<
    typeof AudioChunkSeekBar
  >['ChapterTickMarkRender'];
  AudioChunkTickMarkRender: ComponentProps<
    typeof AudioChunkSeekBar
  >['AudioChunkTickMarkRender'];
}) {
  return (
    <>
      {documentSummary.chapters.map((chapterSummary, chapterIdx) => {
        // Document totals
        const lastChapterChunkSums = durationsPrefixSums.at(-1) ?? [0];
        const estimatedTotalAudioDuration = lastChapterChunkSums.at(-1) ?? 0;

        // Current chapter
        const currentChapterChunkSums = durationsPrefixSums[chapterIdx] ?? [0];
        const currentChapterEstimatedDuration =
          currentChapterChunkSums.at(-1) ?? 0;

        const chapterEndMarkPercent =
          (currentChapterEstimatedDuration * 100) / estimatedTotalAudioDuration;

        // Prev chapter
        const prevChapterChunkSums = durationsPrefixSums[chapterIdx - 1] ?? [0];
        const prevChapterEstimatedDuration = prevChapterChunkSums.at(-1) ?? 0;

        const prevChapterEndMarkPercent =
          (prevChapterEstimatedDuration * 100) / estimatedTotalAudioDuration;

        return (
          <Fragment key={chapterSummary.id}>
            {AudioChunkTickMarkRender && (
              <ChapterChunkTickMarkRenderer
                AudioChunkTickMarkRender={AudioChunkTickMarkRender}
                chapterEndMarkPercent={chapterEndMarkPercent}
                chapterIdx={chapterIdx}
                chapterSummary={chapterSummary}
                durationsPrefixSums={durationsPrefixSums}
                prevChapterEndMarkPercent={prevChapterEndMarkPercent}
              />
            )}
            {ChapterTickMarkRender && (
              <ChapterTickMarkRender
                chapterIdx={chapterIdx}
                chapterSummary={chapterSummary}
                style={{
                  ...baseTickStyleProps,
                  left: `${chapterEndMarkPercent.toFixed(2)}%`,
                }}
              />
            )}
          </Fragment>
        );
      })}
    </>
  );
}

function ChapterChunkTickMarkRenderer({
  chapterSummary,
  chapterIdx,
  AudioChunkTickMarkRender,
  chapterEndMarkPercent,
  prevChapterEndMarkPercent,
  durationsPrefixSums,
}: {
  chapterSummary: ChapterSummary;
  chapterIdx: number;
  AudioChunkTickMarkRender: NonNullable<
    ComponentProps<typeof AudioChunkSeekBar>['AudioChunkTickMarkRender']
  >;
  chapterEndMarkPercent: number;
  prevChapterEndMarkPercent: number;
  durationsPrefixSums: Array<Array<number>>;
}) {
  const currentChapterChunkSums = durationsPrefixSums[chapterIdx] ?? [0];
  const prevChapterChunkSums = durationsPrefixSums[chapterIdx - 1] ?? [0];

  const currentChapterEndPosMs = currentChapterChunkSums.at(-1) ?? 0;
  const prevChapterEndPosMs = prevChapterChunkSums.at(-1) ?? 0;
  const currentChapterEstimatedDuration =
    currentChapterEndPosMs - prevChapterEndPosMs;

  return chapterSummary.tts_ids.map((ttsId, ttsIdx) => {
    const ttsEndPosMs = currentChapterChunkSums[ttsIdx] ?? 0;
    const posIntoChapter = ttsEndPosMs - prevChapterEndPosMs;
    const percentageOfCurrentChapter =
      assertDefined(posIntoChapter) / currentChapterEstimatedDuration;

    const ttsEndMarkPercent =
      prevChapterEndMarkPercent +
      (chapterEndMarkPercent - prevChapterEndMarkPercent) *
        percentageOfCurrentChapter;

    return (
      <AudioChunkTickMarkRender
        chapterIdx={chapterIdx}
        chapterSummary={chapterSummary}
        key={ttsId}
        style={{
          ...baseTickStyleProps,
          left: `${ttsEndMarkPercent.toFixed(2)}%`,
        }}
        ttsId={ttsId}
        ttsIdx={ttsIdx}
      />
    );
  });
}
